import replacePlaceholderValue from '../../utils/ReplacePlaceholderValue';
import { createComponentStore } from '../../utils/createComponentStore';

const keyAuto = () => 'list' + (Math.floor(Math.random() * 90000000) + 10000).toString();

const componentData = () => ({
  key: keyAuto(),
  func: null,
  list: [],
  replace: [],
  withThis: [],
  present: [],
  followList: [],
  listBelow: [],
  newList: [],
  filterList: [],
  duplicateList: [],
  conditions: [],
  conditionIsMet: [],
  extraction: [],
  rearrange: [],
  splitList: [],
  exportPlaceholders: [{ key: keyAuto() }],
  numPlaceholder: 1,
});

const initData = () => [componentData()];

const { createStore, getStore, defaultStore } = createComponentStore({
  type: 'list_transform',
  endpoint: 'list-transform',
  registryKey: 'list-transform',
  initData,

  // data is an array, so the default object-spread strategy won't work;
  // use the raw value directly when present, otherwise fall back to initData.
  onLoadData: (rawData, component) => {
    const cd = component.componentData;
    if (cd && Array.isArray(cd.data)) return cd.data;
    if (Array.isArray(rawData)) return rawData;
    return initData();
  },

  extraState: () => ({
    position: null,
    field: null,
    posiCondition: null,
    posiConditionIsMet: null,
  }),

  extraActions: (set, get) => ({
    setComponentName: (name) => set({ name }),

    setFocus: (index, field) =>
      set({ position: index, field, posiCondition: null, posiConditionIsMet: null }),

    setFocusCondition: (index, posi, type) => {
      const updates = { position: index };
      if (type === 'conditions') updates.posiCondition = posi;
      if (type === 'conditionIsMet') updates.posiConditionIsMet = posi;
      set(updates);
    },

    setComponent: (value, index, type) => {
      set((s) => {
        const data = s.data.map((item, i) => {
          if (i !== index) return item;
          const updated = { ...item };
          switch (type) {
            case 'key':
              updated.key = value;
              break;
            case 'func': {
              let conditions = [];
              let conditionIsMet = [];
              let extraction = [];
              let outputType = 'list';
              let rearrange = [];

              const conditionDefault = [{ logic: 'undefined', func: null, value: [] }];
              const extractionDefault = [{ func: 'the_first', elementNo: 1 }];
              const rearrangeDefault = [{ func: 'ascending_order' }];

              if (value === 'filter_based_on_a_conditional') {
                conditions = conditionDefault;
              }
              if (value === 'count_if_conditional_is_met') {
                conditionIsMet = conditionDefault;
              }
              if (value === 'extract_by_order') {
                extraction = extractionDefault;
              }
              if (value === 'rearrange') {
                rearrange = rearrangeDefault;
              }
              if (
                value === 'count_a_specific_value' ||
                value === 'count_if_conditional_is_met' ||
                value === 'count_length'
              ) {
                outputType = 'number';
              }

              return {
                key: updated.key,
                func: value,
                outputType,
                list: [],
                replace: [],
                withThis: [],
                present: [],
                followList: [],
                listBelow: [],
                newList: [],
                filterList: [],
                duplicateList: [],
                splitList: [],
                exportPlaceholders: [{ key: keyAuto() }],
                numPlaceholder: 1,
                conditions,
                conditionIsMet,
                extraction,
                rearrange,
              };
            }
            case 'list':
              if (value) updated.list = [value];
              break;
            case 'replace':
              if (value) updated.replace = [value];
              break;
            case 'withThis':
              if (value) updated.withThis = [value];
              break;
            case 'present':
              if (value) updated.present = [value];
              break;
            case 'followList':
              if (value) updated.followList = [value];
              break;
            case 'listBelow':
              if (value) updated.listBelow = [value];
              break;
            case 'duplicateList':
              if (value) updated.duplicateList = [value];
              break;
            case 'splitList':
              if (value) updated.splitList = [value];
              break;
            case 'newList':
              if (value) {
                updated.newList = [...updated.newList, value];
              }
              break;
            default:
              break;
          }
          return updated;
        });

        const patchForFunc =
          type === 'func'
            ? { posiCondition: null, position: null, posiConditionIsMet: null }
            : {};
        return { data, ...patchForFunc };
      });
    },

    setCondition: (value, index, position, field, type) => {
      set((s) => {
        const data = s.data.map((item, i) => {
          if (i !== index) return item;
          const updated = { ...item };
          if (type === 'conditions') {
            const conditions = [...updated.conditions];
            conditions[position] = {
              ...conditions[position],
              [field]: field === 'value' && value ? [value] : value,
            };
            updated.conditions = conditions;
          }
          if (type === 'conditionIsMet') {
            const conditionIsMet = [...updated.conditionIsMet];
            conditionIsMet[position] = {
              ...conditionIsMet[position],
              [field]: field === 'value' && value ? [value] : value,
            };
            updated.conditionIsMet = conditionIsMet;
          }
          return updated;
        });
        return { data };
      });
    },

    setExtraction: (value, index, field) => {
      set((s) => {
        const data = s.data.map((item, i) => {
          if (i !== index) return item;
          const updated = { ...item };
          const extraction = [...updated.extraction];
          extraction[0] = { ...extraction[0] };
          if (field === 'func') extraction[0].func = value;
          if (field === 'value') extraction[0].elementNo = value;
          updated.extraction = extraction;
          return updated;
        });
        return { data };
      });
    },

    setRearrange: (value, index, field) => {
      set((s) => {
        const data = s.data.map((item, i) => {
          if (i !== index) return item;
          const updated = { ...item };
          const rearrange = [...updated.rearrange];
          rearrange[0] = { ...rearrange[0] };
          if (field === 'func') rearrange[0].func = value;
          updated.rearrange = rearrange;
          return updated;
        });
        return { data };
      });
    },

    addComponent: () => {
      set((s) => ({ data: [...s.data, componentData()] }));
    },

    addCondition: (index, type) => {
      const cond = { logic: 'OR', func: null, value: [] };
      set((s) => {
        const data = s.data.map((item, i) => {
          if (i !== index) return item;
          const updated = { ...item };
          if (type === 'conditions') updated.conditions = [...updated.conditions, cond];
          if (type === 'conditionIsMet') updated.conditionIsMet = [...updated.conditionIsMet, cond];
          return updated;
        });
        return { data };
      });
    },

    delComponent: (index) => {
      set((s) => ({ data: s.data.filter((_, i) => i !== index) }));
    },

    deleteCondition: (index, position, type) => {
      set((s) => {
        const data = s.data.map((item, i) => {
          if (i !== index) return item;
          const updated = { ...item };
          if (type === 'conditions')
            updated.conditions = updated.conditions.filter((_, p) => p !== position);
          if (type === 'conditionIsMet')
            updated.conditionIsMet = updated.conditionIsMet.filter((_, p) => p !== position);
          return updated;
        });
        return { data };
      });
    },

    deleteChipCondition: (indexChip, index, p, type) => {
      set((s) => {
        const data = s.data.map((item, i) => {
          if (i !== index) return item;
          const updated = { ...item };
          if (type === 'conditions') {
            const conditions = [...updated.conditions];
            const vals = [...conditions[p].value];
            vals.splice(indexChip, 1);
            conditions[p] = { ...conditions[p], value: vals };
            updated.conditions = conditions;
          }
          if (type === 'conditionIsMet') {
            const conditionIsMet = [...updated.conditionIsMet];
            const vals = [...conditionIsMet[p].value];
            vals.splice(indexChip, 1);
            conditionIsMet[p] = { ...conditionIsMet[p], value: vals };
            updated.conditionIsMet = conditionIsMet;
          }
          return updated;
        });
        return { data };
      });
    },

    deleteChip: (indexChip, index, field) => {
      set((s) => {
        const data = s.data.map((item, i) => {
          if (i !== index) return item;
          const updated = { ...item };
          if (updated[field]) {
            const arr = [...updated[field]];
            arr.splice(indexChip, 1);
            updated[field] = arr;
          }
          return updated;
        });
        return { data };
      });
    },

    setSplitList: (index, exportPlaceholders, numPlaceholder) => {
      set((s) => {
        const data = s.data.map((item, i) => {
          if (i !== index) return item;
          return { ...item, exportPlaceholders, numPlaceholder };
        });
        return { data };
      });
    },

    handleExportDifferentNumberOfPlaceholders: (event, index) => {
      const value = event.target.value;
      set((s) => {
        const data = s.data.map((item, i) => {
          if (i !== index) return item;
          const updated = { ...item };
          if (value > 0 && value < 101) {
            if (value > updated.numPlaceholder) {
              const addNum = value - updated.numPlaceholder;
              const arr = [...updated.exportPlaceholders];
              for (let j = 0; j < addNum; j++) arr.push({ key: keyAuto() });
              updated.exportPlaceholders = arr;
              updated.numPlaceholder = value;
            } else if (value < updated.numPlaceholder) {
              const removeNum = updated.numPlaceholder - value;
              const arr = [...updated.exportPlaceholders];
              for (let j = 0; j < removeNum; j++) arr.pop();
              updated.exportPlaceholders = arr;
              updated.numPlaceholder = value;
            }
          } else {
            updated.numPlaceholder = value;
            updated.exportPlaceholders = [];
          }
          return updated;
        });
        return { data };
      });
    },

    handleChangePlaceholderName: (event, key, index) => {
      const value = replacePlaceholderValue(event.target.value);
      set((s) => {
        const data = s.data.map((item, i) => {
          if (i !== index) return item;
          const updated = { ...item };
          const placeholders = [...updated.exportPlaceholders];
          placeholders[key] = { ...placeholders[key], key: value };
          updated.exportPlaceholders = placeholders;
          return updated;
        });
        return { data };
      });

      const state = get();
      const item = state.data[index];
      if (item) {
        get().setSplitList(index, item.exportPlaceholders, item.numPlaceholder);
      }
    },

    // Override getTemplate to include UI-only state fields
    getTemplate: () => {
      const s = get();
      return {
        componentId: s.componentId,
        name: s.name,
        position: s.position,
        data: s.data,
        type: s.type,
        field: s.field,
        lastModified: s.lastModified,
        posiCondition: s.posiCondition,
        posiConditionIsMet: s.posiConditionIsMet,
      };
    },
  }),
});

export function getTransformStore(componentId) {
  return getStore(componentId);
}

export default defaultStore;
